// =====================
// DOM Manipulation Utils
// =====================
function l_smoothScrollOnAnchorTags() {
	document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
		const id = anchor.getAttribute('href').split('#')?.[1]
		if (!id) return

		anchor.addEventListener('click', (e) => {
			e.preventDefault()

			const target = document.querySelector(anchor.getAttribute('href'))
			target?.scrollIntoView?.({ behavior: 'smooth', block: 'center', inline: 'center' })
		})
	})
}

// =====================
// Focus Management & Trap Focus
// =====================

function isVisible(element) {
	if (!element) return false

	if (element.closest('[aria-hidden="true"], [hidden]')) return false

	const detailsParent = element.closest('details')
	if (detailsParent && !detailsParent.open && !detailsParent.contains(element.closest('summary'))) {
		return false
	}

	if (element.offsetParent === null && getComputedStyle(element).position !== 'fixed') {
		return false
	}

	const style = getComputedStyle(element)
	if (style.visibility === 'hidden' || style.visibility === 'collapse') return false

	if (element.getClientRects().length === 0) return false

	return true
}

function l_getFocusableElements(container) {
	const focusableSelectors = `summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe`

	return Array.from(container.querySelectorAll(focusableSelectors)).filter((el) => isVisible(el))
}

function l_disableFocusableElements(container) {
	l_getFocusableElements(container).forEach((el) => {
		el.setAttribute('tabindex', '-1')
		el.setAttribute('aria-disabled', 'true')
	})
}

function l_enableFocusableElements(container) {
	l_getFocusableElements(container).forEach((el) => {
		el.removeAttribute('tabindex')
		el.removeAttribute('aria-disabled')
	})
}

const l_trapFocusHandlers = {}

function l_trapFocus(container, defaultElementToFocus, enableFocusRange = true) {
	// cleanup any previous traps
	l_removeTrapFocus()

	l_trapFocusHandlers.keydown = (event) => {
		const key = event.code?.toUpperCase()
		const isTab = key === 'TAB'
		const isArrowDown = key === 'ARROWDOWN'
		const isArrowUp = key === 'ARROWUP'

		const elements = l_getFocusableElements(container)
		const [first, last] = [elements[0], elements[elements.length - 1]]
		const currentIndex = elements.indexOf(event.target)

		if (!elements.length) return

		if (isTab) {
			if ((event.target === last || last?.contains?.(event.target)) && !event.shiftKey) {
				event.preventDefault()
				first?.focus()
			} else if ((event.target === container || first?.contains?.(event.target)) && event.shiftKey) {
				event.preventDefault()
				last?.focus()
			}

			return
		}

		if (isArrowDown) {
			event.preventDefault()
			if (currentIndex === -1 || currentIndex === elements.length - 1) {
				first?.focus()
			} else {
				elements[currentIndex + 1]?.focus()
			}

			return
		}

		if (isArrowUp) {
			event.preventDefault()
			if (currentIndex === -1 || currentIndex === 0) {
				last?.focus()
			} else {
				elements[currentIndex - 1]?.focus()
			}

			return
		}
	}

	l_trapFocusHandlers.focusin = (event) => {
		if (container.contains(event.target)) {
			document.addEventListener('keydown', l_trapFocusHandlers.keydown)
		}
	}

	l_trapFocusHandlers.focusout = () => {
		document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	}

	document.addEventListener('focusin', l_trapFocusHandlers.focusin)
	document.addEventListener('focusout', l_trapFocusHandlers.focusout)

	// Initial focus
	const elements = l_getFocusableElements(container)
	const first = elements[0]
	const elementToFocus = defaultElementToFocus || first
	if (elementToFocus) {
		elementToFocus.focus()
		if (
			elementToFocus.tagName === 'INPUT' &&
			['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
			elementToFocus.value &&
			enableFocusRange
		) {
			elementToFocus.setSelectionRange(0, elementToFocus.value.length)
		}
	}
}

function l_removeTrapFocus(elementToFocus = null) {
	document.removeEventListener('focusin', l_trapFocusHandlers.focusin)
	document.removeEventListener('focusout', l_trapFocusHandlers.focusout)
	document.removeEventListener('keydown', l_trapFocusHandlers.keydown)
	if (elementToFocus) elementToFocus.focus()
}

// =====================
// Event/Utility Functions
// =====================

function l_debounce(fn, wait) {
	let t

	return (...args) => {
		clearTimeout(t)
		t = setTimeout(() => fn.apply(this, args), wait)
	}
}

function l_addEventDelegate({
	context = document.documentElement,
	event = 'click',
	selector,
	handler,
	capture = false,
}) {
	if (!context) return

	const listener = function (e) {
		// loop parent nodes from the target to the delegation node
		for (let target = e.target; target && target !== this; target = target.parentNode) {
			if (target.matches(selector)) {
				handler.call(target, e, target)
				break
			}
		}
	}

	context.addEventListener(event, listener, capture)

	return () => {
		context.removeEventListener(event, listener, capture)
	}
}

function l_fetchConfig(type = 'json') {
	return {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
	}
}

// =====================
// Money Formatting
// =====================

function l_formatMoney(cents, format = '') {
	let value = ''
	const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/
	const formatString = format || window.LTATheme.money.format || '${{amount}}'

	if (typeof cents == 'string') {
		cents = cents.replace('.', '')
	}

	function defaultOption(opt, def) {
		return typeof opt == 'undefined' ? def : opt
	}

	function formatWithDelimiters(number, precision, thousands, decimal) {
		precision = defaultOption(precision, 2)
		thousands = defaultOption(thousands, ',')
		decimal = defaultOption(decimal, '.')

		if (isNaN(number) || number == null) {
			return 0
		}

		number = (number / 100.0).toFixed(precision)

		var parts = number.split('.'),
			dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands),
			cents = parts[1] ? decimal + parts[1] : ''

		return dollars + cents
	}

	switch (formatString.match(placeholderRegex)[1]) {
		case 'amount':
			value = formatWithDelimiters(cents, 2)
			break
		case 'amount_no_decimals':
			value = formatWithDelimiters(cents, 0, ' ', '.')
			break
		case 'amount_with_comma_separator':
			value = formatWithDelimiters(cents, 2, '.', ',')
			break
		case 'amount_no_decimals_with_comma_separator':
			value = formatWithDelimiters(cents, 0, '.', '.')
			break
	}

	return formatString.replace(placeholderRegex, value)
}

/************************************************************************************

* [ADD TO CART MODULE]

*************************************************************************************/

class LTACartServices {
	constructor(cartDrawer = null) {
		this.cartDrawer = cartDrawer
	}

	getBaseConfig() {
		return {
			method: 'POST',
			headers: {
				'X-Requested-With': 'XMLHttpRequest',
			},
		}
	}

	createConfigFromForm(formEl) {
		const config = this.getBaseConfig()
		const formData = new FormData(formEl)

		config.body = formData

		return config
	}

	createConfigFromItems(items = []) {
		const config = this.getBaseConfig()
		const formData = new FormData()

		items.forEach((item, index) => {
			const prefix = `items[${index}]`

			formData.append(`${prefix}[id]`, item.id)
			formData.append(`${prefix}[quantity]`, item.quantity)

			if (item.selling_plan) {
				formData.append(`${prefix}[selling_plan]`, item.selling_plan)
			}

			// Handle properties as nested fields
			if (item.properties && typeof item.properties === 'object') {
				Object.entries(item.properties).forEach(([key, value]) => {
					if (value !== undefined && value !== null) {
						formData.append(`${prefix}[properties][${key}]`, value)
					}
				})
			}
		})

		config.body = formData
		return config
	}

	async addToCart(config, id = 'default') {
		// Note: Don't use finally / catch here

		return fetch(`${window.LTATheme.routes.cart_add_url}.js`, config)
			.then((res) => {
				return res.json()
			})
			.then((data) => {
				if (data?.status) {
					document.dispatchEvent(new CustomEvent('l:cart:error', { detail: { id, description: data.description } }))

					return data
				}

				document.dispatchEvent(new CustomEvent('l:product:added', { bubbles: true, detail: { id, response: data } }))

				return data
			})
	}

	async changeCartLineItem(formData) {
		return fetch(`${window.LTATheme.routes.cart_change_url}.js`, { ...l_fetchConfig(), body: JSON.stringify(formData) })
			.then((res) => res.json())
			.then((data) => {
				return data
			})
	}
}

const ltaCartService = new LTACartServices()

/************************************************************************************

* [GENERAL COMPONENTS]

*************************************************************************************/

/*****************************************
 * [COMPONENT]: SWIPER CAROUSEL
 ******************************************/

class LTASwiperCarousel extends HTMLElement {
	constructor() {
		super()
		this._onResize = this._onResize.bind(this)
		this._swiperInitialized = false
	}

	connectedCallback() {
		if (window?.Swiper) {
			this.setupCarousel()
			return
		}

		document.addEventListener('l:vendor:loaded', () => {
			this.setupCarousel()
		})
	}

	disconnectedCallback() {
		window.removeEventListener('resize', this._onResize)
	}

	setupCarousel() {
		const configNode = this.querySelector('[data-carousel-config]')
		if (!configNode) return

		this.carouselConfig = JSON.parse(configNode.textContent)
		if (!this.carouselConfig) return

		this.swiperSlideEls = this.querySelectorAll('.swiper-slide')

		this.classList.remove('is-placeholder')
		this.swiperSlideEls.forEach((swiperSlide) => swiperSlide.classList.remove('l-hidden'))

		this.swiperCarousel = new window.Swiper(this, this.carouselConfig)
		this._swiperInitialized = true

		// Design mode scroll
		if (window.Shopify?.designMode) {
			this.designModeScroll()
		}

		// Set up navigation top position
		const hasNavigationTarget = this.carouselConfig?.navigation && this.carouselConfig.navigation.navigationHeightTarget
		if (hasNavigationTarget) {
			this.setupNavigation()
			window.addEventListener('resize', this._onResize)
		}

		// Dispatch event
		if (this.dataset.dispatchEvent === 'true') {
			this.dispatchEvent(
				new CustomEvent('l:swiper:carousel:loaded', {
					bubbles: true,
					detail: {
						id: this.id || this.dataset.id || 'default',
					},
				})
			)
		}
	}

	_onResize() {
		// Re-setup navigation when window resizes
		if (this._swiperInitialized && this.carouselConfig?.navigation?.navigationHeightTarget) {
			this.setupNavigation()
		}
	}

	setupNavigation() {
		const navigation = this.carouselConfig?.navigation
		if (!navigation || !navigation.navigationHeightTarget) return

		const targetEl = this.querySelector(navigation.navigationHeightTarget)
		if (!targetEl) return

		const navTop = `${targetEl.offsetHeight / 2}px`

		const nextNavEls = document.querySelectorAll(navigation.nextEl)
		const prevNavEls = document.querySelectorAll(navigation.prevEl)

		nextNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
		prevNavEls.forEach((el) => el.style.setProperty('--l-swiper-top', navTop))
	}

	designModeScroll() {
		document.addEventListener('shopify:block:select', (event) => {
			const targetEl = event.target

			this.swiperSlideEls.forEach((swiperSlide, index0) => {
				if (targetEl === swiperSlide) this.slideTo(index0)
			})
		})
	}

	slideTo(index = 0) {
		if (this.swiperCarousel && typeof this.swiperCarousel.slideTo === 'function') {
			this.swiperCarousel.slideTo(index)
		}
	}
}

customElements.define('l-swiper-carousel', LTASwiperCarousel)

/*****************************************
 * [COMPONENT]: ATC BUTTON
 ******************************************/

class LTACTAButton extends HTMLElement {
	constructor() {
		super()

		this.handleAtcBtnClick = this.handleAtcBtnClick.bind(this)
	}

	connectedCallback() {
		this.buttonEl = this.querySelector('button')
		if (this.buttonEl) {
			this.buttonEl.addEventListener('click', this.handleAtcBtnClick)
		}
	}

	disconnectedCallback() {
		if (this.buttonEl) {
			this.buttonEl.removeEventListener('click', this.handleAtcBtnClick)
		}
	}

	handleAtcBtnClick() {
		if (!this.buttonEl) return

		this.buttonEl.classList.add('is-loading', 'l-button-loading')

		const { variantId, quantity, sellingPlanId } = this.dataset
		const config = {
			id: variantId,
			quantity: parseInt(quantity),
			...(sellingPlanId ? { selling_plan: sellingPlanId } : {}),
			properties: {
				'is-package-box': 'true',
			},
		}

		const formData = ltaCartService.createConfigFromItems([config])

		ltaCartService
			.addToCart(formData, 'l-atc-button')
			.catch(console.error)
			.finally(() => {
				this.buttonEl.classList.remove('is-loading', 'l-button-loading')
			})
	}
}

customElements.define('l-atc-button', LTACTAButton)

/*****************************************
 * [COMPONENT]: COLLAPSIBLE TAB
 ******************************************/

class LTACollapsibleTab extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.triggerEl = this.querySelector('[data-trigger]')
		this.contentEl = this.querySelector('[data-content]')

		// Data
		this.expandedClass = 'is-expanded'
		this.collapsedClass = 'is-collapsed'
		this.collapsedHeight = '0px'
		this.oneAtATime = true
		this.isScrollIntoView = this.dataset.scrollIntoView === 'true'

		this.transitionendEvent = this.transitionendEventName()

		// Setup default behavior / data
		this.dataset.oneOpen === 'false' && (this.oneAtATime = false)

		if (this.getAttribute('open') === 'true') {
			this.selected = true
			this.contentEl.removeAttribute('hidden')
			this.contentEl.classList.remove('l-hidden')
			this.classList.add(this.expandedClass)
			this.setExpandedAria(false)
		} else {
			this.contentEl.style.height = this.collapsedHeight
			this.classList.add(this.collapsedClass)
			this.setCollapsedAria(null, false)
		}

		// Events
		this.triggerEl.addEventListener('click', (e) => this.toggle(e))
	}

	toggle(e) {
		const { preventDefault, onlyOpen } = this.dataset

		if (preventDefault !== 'false') {
			e.preventDefault()
		}

		const shouldOnlyOpen = onlyOpen === 'true'

		if (shouldOnlyOpen) {
			this.selected ? this.close() : this.open()
		} else {
			this.open()
		}

		// Ensure only one tab is open in the group if required.
		if (this.selected && this.oneAtATime) {
			const tabsContainer = this.closest('[data-collapsible-wrapper]')
			if (tabsContainer) {
				const collapsibleTabs = tabsContainer.querySelectorAll('l-collapsible-tab')
				collapsibleTabs.forEach((tab) => {
					if (tab !== this && tab.selected && tab.close) {
						tab.close()
					}
				})
			}
		}
	}

	open() {
		this.selected = true
		this.expand()

		this.setExpandedAria()
	}

	close() {
		this.selected = false
		this.collapse()

		this.setCollapsedAria()
	}

	expand() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				this.isOpen &&
					requestAnimationFrame(() => {
						this.contentEl.style.transition = '0'
						this.contentEl.style.height = 'auto'

						requestAnimationFrame(() => {
							this.contentEl.style.height = null
							this.contentEl.style.transition = null
						})
					})
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = true

		this.contentEl.removeAttribute('hidden')
		this.contentEl.classList.remove('l-hidden')

		this.classList.add(this.expandedClass)
		this.classList.remove(this.collapsedClass)

		this.contentEl.style.height = this.contentEl.scrollHeight + 'px'
	}

	collapse() {
		const onTransitionEnd = ((event) => {
			if (event.target === this.contentEl) {
				this.contentEl.removeEventListener(this.transitionendEvent, onTransitionEnd)

				if (!this.isOpen) {
					this.classList.add(this.collapsedClass)
				}

				this.contentEl.setAttribute('hidden', 'hidden')
				this.contentEl.classList.add('l-hidden')
			}
		}).bind(this)

		this.contentEl.addEventListener(this.transitionendEvent, onTransitionEnd)
		this.isOpen = false
		this.classList.remove(this.expandedClass)

		requestAnimationFrame(() => {
			this.contentEl.style.transition = '0'
			this.contentEl.style.height = this.contentEl.scrollHeight + 'px'

			requestAnimationFrame(() => {
				this.contentEl.style.transition = null
				this.contentEl.style.height = this.collapsedHeight
			})
		})

		if (this.isScrollIntoView) {
			l_debounce(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}, 400)()
		}
	}

	destroy() {
		this.triggerEl && this.triggerEl.removeEventListener('click', (e) => this.toggle(e))
		this.contentEl && this.contentEl.removeAttribute('aria-hidden')
		this.contentEl && (this.contentEl.style.height = 'auto')

		this.classList.remove(this.expandedClass, this.collapsedClass)
		this.removeAttribute('open')
	}

	transitionendEventName() {
		let eventName,
			testElement = document.createElement('div'),
			eventNames = {
				transition: 'transitionend',
				OTransition: 'otransitionend',
				MozTransition: 'transitionend',
				WebkitTransition: 'webkitTransitionEnd',
			}

		for (eventName in eventNames) {
			if (eventNames.hasOwnProperty(eventName) && testElement.style[eventName] !== false) {
				return eventNames[eventName]
			}
		}
	}

	setExpandedAria(autoFocus = true) {
		this.setAttribute('open', true)

		this.triggerEl.setAttribute('aria-expanded', 'true')
		this.contentEl.removeAttribute('aria-hidden')
		this.contentEl.removeAttribute('tabindex')

		if (autoFocus) {
			l_enableFocusableElements(this.contentEl)
			l_trapFocus(this)
		}
	}

	setCollapsedAria(elementToFocus = this.triggerEl, enableTrapFocus = true) {
		this.removeAttribute('open')

		this.triggerEl.setAttribute('aria-expanded', 'false')
		this.contentEl.setAttribute('aria-hidden', 'true')
		this.contentEl.setAttribute('tabindex', '-1')

		l_disableFocusableElements(this.contentEl)
		enableTrapFocus && l_removeTrapFocus(elementToFocus)
	}
}

customElements.define('l-collapsible-tab', LTACollapsibleTab)

/*****************************************
 * [COMPONENT]: QUANTITY INPUT
 ******************************************/

if (!customElements.get('l-qty-input')) {
	class LTAQtyInput extends HTMLElement {
		constructor() {
			super()

			this.onButtonClick = this.onButtonClick.bind(this)
			this.onInputChange = this.onInputChange.bind(this)
			this.onInputBeforeChange = this.onInputBeforeChange.bind(this)
		}

		connectedCallback() {
			this.input = this.querySelector('input')
			this.buttonMinus = this.querySelector("button[name='minus']")
			this.buttonPlus = this.querySelector("button[name='plus']")

			if (!this.input) return

			this.changeEvent = new Event('change', { bubbles: true })

			this.validateQtyRules()
			this.setupEventListeners()
		}

		setupEventListeners() {
			this.input.addEventListener('input', this.onInputBeforeChange)
			this.input.addEventListener('change', this.onInputChange)
			this.querySelectorAll('button').forEach((button) => button.addEventListener('click', this.onButtonClick))
		}

		onInputBeforeChange() {
			const value = parseInt(this.input.value)

			if (isNaN(value)) return

			const min = parseInt(this.input.min)
			const max = parseInt(this.input.max)
			let newValue = value

			if (!isNaN(min) && value < min) {
				newValue = min
			}

			if (!isNaN(max) && value > max) {
				newValue = max
			}

			if (newValue !== value) {
				this.input.value = newValue
			}
		}

		onInputChange() {
			this.validateQtyRules()
		}

		onButtonClick(event) {
			event.preventDefault()

			const previousValue = this.input.value
			const buttonName = event.target.name

			if (buttonName === 'plus') {
				this.incrementQuantity()
			} else if (buttonName === 'minus') {
				this.decrementQuantity()
			}

			if (previousValue !== this.input.value) {
				this.input.dispatchEvent(this.changeEvent)
				this.onQuantityChanged(previousValue, parseInt(this.input.value))
			}
		}

		onQuantityChanged(oldValue, newValue) {
			// Override in child classes if needed
		}

		incrementQuantity() {
			const min = parseInt(this.input.dataset.min)
			const step = parseInt(this.input.step)
			const value = parseInt(this.input.value)

			if (min > step && value === 0) {
				this.input.value = min
			} else {
				this.input.stepUp()
			}
		}

		decrementQuantity() {
			this.input.stepDown()

			const min = this.input.dataset.min
			if (min && min === this.input.value) {
				this.input.value = parseInt(this.input.min)
			}
		}

		validateQtyRules() {
			const value = parseInt(this.input.value)

			if (this.input.min && this.buttonMinus) {
				this.buttonMinus.toggleAttribute('disabled', value <= parseInt(this.input.min))
			}

			if (this.input.max && this.buttonPlus) {
				this.buttonPlus.toggleAttribute('disabled', value >= parseInt(this.input.max))
			}
		}

		disconnectedCallback() {
			if (this.input) {
				this.input.removeEventListener('input', this.onInputBeforeChange)
				this.input.removeEventListener('change', this.onInputChange)
			}

			this.querySelectorAll('button').forEach((button) => button.removeEventListener('click', this.onButtonClick))
		}
	}

	customElements.define('l-qty-input', LTAQtyInput)
}

/************************************************************************************

* [SECTION COMPONENTS]

*************************************************************************************/
/*****************************************
 * [COMPONENT]: PDP SUBSCRIPTIONS
 ******************************************/

class LTASubscriptionSellingPlans extends HTMLElement {
	constructor() {
		super()

		this.handleSellingPlanGroupChange = this.handleSellingPlanGroupChange.bind(this)
	}

	connectedCallback() {
		this.addEventListener('change', this.handleSellingPlanGroupChange)
	}

	handleSellingPlanGroupChange(event) {
		if (event.target.name !== 'selling_plan_option_group') return

		const shouldUseDataForm = event.target.value === 'subscription'
		const selectors = 'input[name="selling_plan_qty"], select[name="selling_plan"]'
		const elements = this.querySelectorAll(selectors)

		elements.forEach((el) => {
			if (shouldUseDataForm) {
				el.setAttribute('form', el.getAttribute('data-form'))
				el.removeAttribute('data-form')
			} else {
				el.setAttribute('data-form', el.getAttribute('form'))
				el.removeAttribute('form')
			}
		})
	}
}

customElements.define('l-subscriptions-selling-plans', LTASubscriptionSellingPlans)

/*****************************************
 * [SECTION]: BUILD A BOX SUBSCRIPTIONS
 ******************************************/

class LTABuildABoxSubscriptions extends HTMLElement {
	constructor() {
		super()
	}

	connectedCallback() {
		// Elements
		this.selectedOptionsEl = this.querySelector('[data-selected-options]')
		this.quantityBadgesEl = this.querySelector('[data-quantity-badges]')
		this.sellingPlansEl = this.querySelector('[data-selling-plans]')

		this.tagsEl = this.querySelector('[data-tags]')
		this.productGridEl = this.querySelector('[data-products]')
		this.paginationEl = this.querySelector('[data-pagination]')

		this.atcEl = this.querySelector('[data-atc]')
		this.atcTotalEl = this.atcEl.querySelector('[data-atc-total]')
		this.atcButtonEl = this.atcEl.querySelector('[data-atc-button]')

		// Data
		this.currentSetting = null
		this.selectedProducts = new Map()

		this.fetchBundleData()
	}

	fetchBundleData() {
		const fetchURL = `https://www.skinnymixes.com/tools/bundle-subscriptions/api/bundle/${this.dataset.bundleId}`

		fetch(fetchURL)
			.then((res) => res.json())
			.then((data) => {
				this.init(data)
			})
			.catch((err) => {
				console.error(err)
			})
			.finally(() => {
				this.scrollIntoView({ behavior: 'smooth', block: 'start' })
			})
	}

	init(data) {
		this.classList.remove('is-loading')

		// Data
		this._collectionData(data)

		// Render UI
		this._setPaginationData()
		this._togglePagination()
		this._renderProducts()
		this._renderQuantityBadges()
		this._renderSelectionOptions()
		this._setMaxValueForQtyInputs()
		this._renderTags()

		// Reset
		this._resetData()

		// Events
		this.quantityBadgesEl.addEventListener('change', this.onQuantityChanged.bind(this))
		this.productGridEl.addEventListener('change', this.onProductGridChange.bind(this))
		this.paginationEl.querySelector('button').addEventListener('click', this.onLoadMore.bind(this))

		l_addEventDelegate({
			contentEl: this.selectedOptionsEl,
			selector: '[data-selected-product]',
			event: 'click',
			handler: this.onRemoveSelectedProduct.bind(this),
		})

		l_addEventDelegate({
			contentEl: this.tagsEl,
			selector: '[data-tag]',
			event: 'click',
			handler: this.onFilterProductsByTag.bind(this),
		})

		this.atcButtonEl.addEventListener('click', this.onAddToCart.bind(this))
	}

	_collectionData(data) {
		// Bundle Data
		this.bundleData = data
		this.allProductsData = []
		this.currentSetting = this.bundleData.bundle.Settings.reduce((a, b) => (+a.Size < +b.Size ? a : b))

		this.bundleData.productsInSections.forEach((productsInSection) => {
			// Collection
			productsInSection.productsInCollections.forEach((productInCollection) => {
				this.allProductsData.push(...productInCollection)
			})

			// Individual products
			// Update when needed
		})

		this.currentProductsData = this.allProductsData
	}

	/**********************************
	 * [MODULES]: HEADER
	 *********************************/
	onQuantityChanged() {
		const selectedInputEl = this.quantityBadgesEl.querySelector('input:checked')
		this.currentSetting = this.bundleData.bundle.Settings.find((setting) => setting.Size == selectedInputEl.value)

		this.selectedProducts.clear()
		this._setMaxValueForQtyInputs()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	onRemoveSelectedProduct(event) {
		const productRemoveEl = event.target.closest('[data-selected-product]')
		if (!productRemoveEl) return

		const productId = productRemoveEl.dataset.selectedProduct
		const product = this.selectedProducts.get(productId)

		if (!product) return

		// Update selectedProducts map
		if (product.qty > 1) {
			this.selectedProducts.set(productId, {
				...product,
				qty: product.qty - 1,
			})
		} else {
			this.selectedProducts.delete(productId)
		}

		// Update quantity input related to the removed product
		const inputEl = this.productGridEl.querySelector(`l-qty-input input[data-product-id="${productId}"]`)
		if (inputEl) {
			const currentValue = parseInt(inputEl.value, 10) || 0
			const newValue = Math.max(0, currentValue - 1)
			inputEl.value = newValue
			inputEl.dispatchEvent(new Event('change', { bubbles: true }))
		}
	}

	_renderQuantityBadges() {
		this.quantityBadgesEl.querySelectorAll('label').forEach((badgeEl) => {
			const labelEl = badgeEl.querySelector('[data-label]')
			const savingsEl = badgeEl.querySelector('[data-savings]')
			const inputEl = badgeEl.querySelector('input')

			const matchSetting = this.bundleData.bundle.Settings.find((setting) => setting.Label == labelEl.textContent)

			if (matchSetting) {
				const { DynamicDiscountType, DynamicDiscountAmount } = matchSetting
				const originalText = savingsEl.textContent
				const saveText =
					DynamicDiscountType == 'percentage'
						? `SAVE ${matchSetting.DynamicDiscountAmount}%`
						: `${l_formatMoney(DynamicDiscountAmount * 100)}`

				savingsEl.textContent = originalText != '' ? originalText.replace('[save]', saveText) : saveText

				savingsEl.classList.remove('is-loading')
				inputEl.value = matchSetting.Size
			}
		})
	}

	_setMaxValueForQtyInputs() {
		const maxSize = Number(this.currentSetting.Size) || 0

		// Calculate the total number of selected items
		const selectedQty = Array.from(this.selectedProducts.values()).reduce((sum, prod) => sum + (prod.qty || 0), 0)

		const availableQty = maxSize - selectedQty

		this.querySelectorAll('l-qty-input input').forEach((inputEl) => {
			const currentValue = Number(inputEl.value) || 0
			const originalMax = Number(inputEl.dataset.max) || 0
			const productId = inputEl.dataset.productId

			// Determine the new max for this input
			let newMax

			// When no available quantity left, only allow reducing (not increasing)
			if (availableQty === 0) {
				newMax = currentValue
			} else {
				const allowedMax = availableQty + currentValue
				newMax = originalMax > 0 ? Math.min(originalMax, allowedMax) : allowedMax
			}

			inputEl.max = newMax

			// If product is not selected, ensure the input value is 0
			if (!this.selectedProducts.has(productId)) {
				inputEl.value = 0
			} else {
				inputEl.value = this.selectedProducts.get(productId).qty
				inputEl.closest('[data-product-card]').classList.add('is-selected')
			}

			// Run quantity validation if method exists
			if (inputEl.parentElement && typeof inputEl.parentElement.validateQtyRules === 'function') {
				inputEl.parentElement.validateQtyRules()
			}
		})
	}

	_renderSelectionOptions() {
		let selectedOptionsHTML = ''
		let renderedQty = 0

		const selectedQtyArray = [...this.selectedProducts.values()]
		const maxSize = parseInt(this.currentSetting.Size)

		this.selectedOptionsEl.dataset.maxSize = maxSize

		for (let index = 0; index < maxSize; index++) {
			const matchProduct = selectedQtyArray[index]
			const maxQty = matchProduct?.qty || 1

			for (let _index = 0; _index < maxQty; _index++) {
				renderedQty++
				if (renderedQty > maxSize) break

				selectedOptionsHTML += `
        <div ${matchProduct && `data-selected-product="${matchProduct.id}"`} class="l-sBABox__selectedOptions-item l-column l-relative ${matchProduct ? '' : 'is-placeholder'}">
          <div class="l-sBABox__selectedOptions-media l-media-ratio">${matchProduct?.mediaHTML || ''}</div>
          <span class="l-sBABox__selectedOptions-name l-block l-text l-block-spaces">${matchProduct?.nameHTML || ''}</span>
          ${matchProduct ? `<button class="l-sBABox__selectedOptions-close l-button-reset l-sq l-cursor-pointer l-text l-link-tap l-inline-flex-center l-absolute-position l-zindex-2">X</button>` : ''}
        </div>`
			}
		}

		this.selectedOptionsEl.innerHTML = selectedOptionsHTML
	}

	_resetSelectedProducts() {
		this.selectedProducts.clear()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	_resetData() {
		this.quantityBadgesEl.querySelector('input').checked = true
		this.sellingPlansEl.querySelector('input').checked = true
		this._resetSelectedProducts()
	}

	/**********************************
	 * [MODULES]: TAGS
	 *********************************/

	onFilterProductsByTag(event) {
		const tagEl = event.target.closest('[data-tag]')
		if (!tagEl) return

		// Update active class on tags
		this.tagsEl.querySelectorAll('[data-tag]').forEach((_tagEl) => {
			_tagEl.classList.toggle('is-active', _tagEl === tagEl)
		})

		const selectedTag = tagEl.dataset.filterValue
		this.currentProductsData = selectedTag
			? this.allProductsData.filter((product) => product.productData.tags.some((tag) => tag.includes(selectedTag)))
			: this.allProductsData

		this._renderProducts()
		this._setPaginationData()
		this._togglePagination()
		this._resetSelectedProducts()
	}

	_renderTags() {
		let renderedTagsHTML = ''
		const buildABoxFilterGroups = this.bundleData.bundle.BuildABoxFilterGroups

		buildABoxFilterGroups.forEach((buildABoxFilterGroup) => {
			buildABoxFilterGroup.Filters.forEach(({ FilterType, FilterValue, Name }) => {
				renderedTagsHTML += `
          <button data-tag data-filter-value="${FilterValue}" data-filter-type="${FilterType}" class="l-sBABox__tags-button l-button-reset l-padding l-text l-link-tap l-padding l-relative swiper-slide">
          ${Name}
          </button>
        `
			})
		})

		this.tagsEl.innerHTML = `
      <l-swiper-carousel class="l-sBABox__tags-swiper l-block swiper">
				<div class="swiper-wrapper">
          <button data-tag class="l-sBABox__tags-button l-button-reset l-padding l-text l-link-tap l-padding l-relative swiper-slide is-active">
            ALL FLAVORS
          </button>
          ${renderedTagsHTML}
				</div>

				<script type="application/json" data-carousel-config>
					{
						"slidesPerView": "auto",
						"spaceBetween": 12,
            "centeredSlides": true,
				    "centerInsufficientSlides": true,
				    "centeredSlidesBounds": true
					}
				</script>
			</l-swiper-carousel>
    `
	}

	/**********************************
	 * [MODULES]: PRODUCT CARDS
	 *********************************/
	onProductGridChange(event) {
		const inputEl = event.target
		const productCardEl = inputEl.closest('[data-product-card]')

		const productQty = Number(inputEl.value) || 0
		const productId = productCardEl.dataset.productId

		const maxSize = Number(this.currentSetting.Size) || 0
		const isValidProduct = productQty > 0 && productQty <= maxSize

		if (isValidProduct) {
			const mediaHTML = productCardEl.querySelector('[data-media]')
			const nameHTML = productCardEl.querySelector('[data-name]')
			const matchProductData = this.currentProductsData.find((product) => `${product.id}` == productId)

			this.selectedProducts.set(productId, {
				id: productId,
				mediaHTML: mediaHTML ? mediaHTML.innerHTML : '',
				nameHTML: nameHTML ? nameHTML.textContent : '',
				qty: productQty,
				// Add to cart data
				product: matchProductData,
			})
		} else {
			this.selectedProducts.delete(productId)
		}

		this._setMaxValueForQtyInputs()
		this._renderSelectionOptions()
		this._calculateBundlePrice()
	}

	_renderProducts() {
		this.productGridEl.classList.add('is-loading')

		const products = this.currentProductsData.slice(
			0,
			this.paginationData.currentPage * this.paginationData.itemsPerPage
		)

		const productCardsHTML = products.map((product) => this._renderProductCard(product)).join('')
		this.productGridEl.innerHTML = productCardsHTML

		l_debounce(() => {
			this.productGridEl.classList.remove('is-loading')
		}, 1000)()
	}

	_renderProductCard({ productData }) {
		const defaultVariant = productData.variants[0]
		const firstPreviewImage = productData.media[0].preview_image

		return `
      <div
        class="l-sBABoxCard l-column l-flex l-flex-col" data-product-card
        data-product-id="${productData.id}"
      >
        <div class="l-sBABoxCard__media l-relative l-overflow-hidden l-block-spaces">
          <picture class="l-sBABoxCard__mainMedia l-flex l-media-ratio l-text-none l-overflow-hidden" data-media>
            ${
							firstPreviewImage &&
							`
              <img
                src="${firstPreviewImage.src}&amp;width=2048"
                alt="${productData.title}"
                srcset="
                  ${firstPreviewImage.src}&amp;width=320   320w,
                  ${firstPreviewImage.src}&amp;width=480   480w,
                  ${firstPreviewImage.src}&amp;width=640   640w,
                  ${firstPreviewImage.src}&amp;width=750   750w,
                  ${firstPreviewImage.src}&amp;width=900   900w,
                "
                width="${firstPreviewImage.width}"
                height="${firstPreviewImage.height}"
                sizes="(max-width: 575px) calc(min(100vw, 1440px) / 50%_2),(max-width: 1023px) calc(min(100vw, 1440px) / 50%_2),(min-width: 1024px) calc(min(100vw, 1440px) / 25%_4)"
                fetchpriority="auto"
                loading="eager"
                class="l-sq-full"
              />
              `
						}
          </picture>

          <a
            href="${window.LTATheme.routes.root}products/${productData.handle}"
            aria-label="${productData.title}"
            class="l-sBABoxCard__media-link l-link-overlay l-zindex-3"
          ></a>
        </div>

        <div class="l-sBABoxCard__content l-w-full l-mx-auto l-flex-1 l-flex l-flex-col">
          <div class="l-sBABoxCard__name l-text l-heading-font l-text-center l-block-spaces" data-name>${productData.title}</div>
          <div class="l-sBABoxCard__price l-text l-text-center l-block-spaces">${l_formatMoney(defaultVariant.price)}</div>
          <l-qty-input class="l-sBABoxCard__qty l-flex-center">
            <button
              class="l-sBABoxCard__qty-btn is-decrease l-text l-button-reset l-sq l-inline-flex-center"
              name="minus"
              type="button"
              data-qty-btn
              ${defaultVariant.available == false && 'disabled'}
            >
              -
            </button>

            <input
              id="Quantity-${defaultVariant.id}"
              data-quantity-input
              class="l-sBABoxCard__qty-input l-input l-text-center l-sq l-text"
              type="number"
              value="0"
              step="${defaultVariant.quantity_rule.increment || 1}"
              min="0"
              data-product-id="${productData.id}"
              ${defaultVariant.quantity_rule.max ? `data-max="${defaultVariant.quantity_rule.max}" max="${defaultVariant.quantity_rule.max}"` : ''}
              ${defaultVariant.available == false ? 'disabled' : ''}
            >

            <button
              class="l-sBABoxCard__qty-btn is-increase l-text l-button-reset l-sq l-inline-flex-center"
              name="plus"
              type="button"
              data-qty-btn
              ${defaultVariant.available == false && 'disabled'}
            >
              +
            </button>
          </l-qty-input>
        </div>
      </div>
    `
	}

	/**********************************
	 * [MODULES]: PAGINATION
	 *********************************/
	onLoadMore() {
		this.paginationData.currentPage++

		this._renderProducts()
		this._togglePagination()
		this._setMaxValueForQtyInputs()
	}

	_setPaginationData() {
		this.paginationData = {
			currentPage: 1,
			totalPages: Math.ceil(this.currentProductsData.length / 16),
			itemsPerPage: 16,
		}
	}

	_togglePagination() {
		this.paginationEl.classList.toggle('l-hidden', this.paginationData.currentPage >= this.paginationData.totalPages)
	}

	/**********************************
	 * [MODULES]: ADD TO CART
	 *********************************/
	_calculateBundlePrice() {
		const maxSize = Number(this.currentSetting.Size) || 0

		const selectedProducts = [...this.selectedProducts.values()]
		const totalSelectedProductQty = selectedProducts.reduce((acc, product) => acc + product.qty, 0)

		const totalPrice = selectedProducts.reduce(
			(acc, product) => acc + product.product.productData.variants[0].price * product.qty,
			0
		)

		this.atcTotalEl.textContent = l_formatMoney(totalPrice)

		if (totalSelectedProductQty < maxSize) {
			this.atcButtonEl.setAttribute('disabled', '')
		} else {
			this.atcButtonEl.removeAttribute('disabled')
		}
	}

	onAddToCart() {
		// Handle UI
		this.atcButtonEl.classList.add('is-loading')

		// Handle Data
		const selectedProducts = [...this.selectedProducts.values()]
		const selectedSellingPlanId = this.sellingPlansEl.querySelector('input:checked').value
		const bundleID = `${Math.random().toString(36).slice(2, 18)}`
		const addToCartLineItems = selectedProducts.map((product) => ({
			id: product.product.productData.variants[0].id,
			quantity: product.qty,
			properties: {
				_bundleUid: bundleID,
				_bundleHandle: this.bundleData.bundle.Handle,
				_bundleType: 'dynamicBaB',
				_bundleProductId: this.bundleData.bundle.ProductId,
				_bundleName: this.bundleData.bundle.Name,
				_variantId: this.currentSetting.VariantId,
				_bundleLabel: this.currentSetting.Label,
				_bundleBoxSize: this.currentSetting.Size,
			},
			selling_plan: selectedSellingPlanId,
		}))

		const formData = ltaCartService.createConfigFromItems(addToCartLineItems)
		let hasError = false

		// Add To Cart
		ltaCartService
			.addToCart(formData)
			.catch((err) => {
				console.error(err.message)

				hasError = true
			})
			.finally(() => {
				console.log('ADDED TO CART')
			})

		// Update Discounts
		fetch(`${window.LTATheme.routes.cart_update_url}`, {
			...l_fetchConfig(),
			body: JSON.stringify({ discount: this.currentSetting.DynamicDiscountCode }),
		})
			.then((response) => response.json())
			.catch((error) => {
				hasError = true
			})
			.finally(() => {
				console.log('ADDED DISCOUNTS')
			})

		// Redirect to checkout
		l_debounce(() => {
			this.atcButtonEl.classList.remove('is-loading')

			window.location.href = `${window.LTATheme.routes.root}checkout`
		}, 2000)()
	}
}

customElements.define('l-build-a-box-subscriptions', LTABuildABoxSubscriptions)

/************************************************************************************

* [GLOBAL FUNCTIONALITIES]

*************************************************************************************/

function l_openRebuyCartDrawer() {
  const cartToggleEls = document.querySelectorAll('.js-header-cart-toggle')

  cartToggleEls.forEach(cartToggleEl => {
    cartToggleEl.addEventListener('click', (event) => {
      event.preventDefault()

      if (cartToggleEl.hasAttribute('data-redirect-to-cart-page')) {
        window.location.href = window.LTATheme.routes.cart_url
        return
      }

      window.Rebuy?.SmartCart?.show?.()
    })
  })
}

function l_initializeGlobalEventListeners() {
	// Initialize on DOM ready
	window.addEventListener('DOMContentLoaded', () => {
		l_smoothScrollOnAnchorTags()
    l_openRebuyCartDrawer()
	})
}

l_initializeGlobalEventListeners()

